/**
 * Merge local `Study` records with Drive `StudyIndexEntry` rows, then bucket
 * by the year of the most-recent edit (date desc within each bucket, years
 * desc overall — newest study at the very top).
 *
 * Local data wins over index data on every field: the index is a snapshot
 * from the last upload, while local includes any edits the user made since.
 */

import type { Study } from '@skjv/common';
import type { StudyIndexEntry } from '@/services/study-index-service';

export interface StudyListRow {
  id: string;
  name: string;
  startingLocation: Study['startingLocation'];
  endingLocation?: Study['startingLocation'];
  date: string;
  studentCount: number;
  sceneCount: number;
  hasTeacherNotes: boolean;
  source: 'local' | 'remote' | 'both';
  localStudy?: Study;
}

export interface YearGroup {
  year: number;
  rows: StudyListRow[];
}

export function buildYearGroups(
  studies: Study[],
  indexEntries: StudyIndexEntry[],
): YearGroup[] {
  const byId = new Map<string, StudyListRow>();

  for (const entry of indexEntries) {
    byId.set(entry.studyId, indexRow(entry));
  }
  for (const study of studies) {
    const existing = byId.get(study.id);
    byId.set(study.id, mergeLocal(study, existing));
  }

  const buckets = new Map<number, StudyListRow[]>();
  for (const row of byId.values()) {
    const year = new Date(row.date).getFullYear();
    if (Number.isNaN(year)) continue;
    if (!buckets.has(year)) buckets.set(year, []);
    buckets.get(year)!.push(row);
  }

  const groups: YearGroup[] = [];
  for (const [year, rows] of buckets) {
    rows.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    groups.push({ year, rows });
  }
  groups.sort((a, b) => b.year - a.year);
  return groups;
}

function indexRow(entry: StudyIndexEntry): StudyListRow {
  return {
    id: entry.studyId,
    name: entry.name,
    startingLocation: entry.startingLocation,
    endingLocation: entry.endingLocation,
    date: entry.date,
    studentCount: entry.students.length,
    sceneCount: 0,
    hasTeacherNotes: entry.hasTeacherNotes,
    source: 'remote',
  };
}

function mergeLocal(study: Study, indexed: StudyListRow | undefined): StudyListRow {
  const sceneCount = study.scenes.filter((s) => s.descriptions.length > 0).length;
  return {
    id: study.id,
    name: study.name,
    startingLocation: study.startingLocation,
    endingLocation: study.endingLocation,
    date: study.updatedAt ?? indexed?.date ?? new Date().toISOString(),
    studentCount: study.assignments.length,
    sceneCount,
    hasTeacherNotes: indexed?.hasTeacherNotes ?? sceneCount > 0,
    source: indexed ? 'both' : 'local',
    localStudy: study,
  };
}
